import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Button, ButtonVariant } from '../../common/Button'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../../common/Icon'
import { reverseUrl } from '../../../navigation/reverseUrl'
import useShowGettingStarted from '../../../views/society/GettingStartedView/useShowGettingStarted'
import {
  CONSTANTS_SOCIETY_GUIDES,
  setGuideStep,
  societyGuidesQueries,
  TSocietyGuideStepGettingStarted,
} from '../../../api/society-guides'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'

const GettingStarted = ({
  title,
  description,
  gettingStartedId,
  className,
  hideCompleteButton,
}: {
  title: string
  description: string
  className?: string
  hideCompleteButton?: boolean
  gettingStartedId: TSocietyGuideStepGettingStarted
}): React.ReactNode => {
  const { showGettingStarted, society } = useShowGettingStarted()
  const { translate } = useAppTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const authUserId = useAuthenticatedUserId()
  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)
  const { data: guide } = useQuery(
    societyGuidesQueries.gettingStarted(society?._id, currentUserIsAdmin)
  )
  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      // eslint-disable-next-line @typescript-eslint/no-shadow
      societyId,
      id,
      completed,
    }: {
      societyId: string
      id: string
      completed: boolean
    }) => {
      return setGuideStep({
        societyId,
        id,
        completed,
        guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED,
      })
    },
    onSuccess: (response, variables) => {
      const { data } = response.data
      if (data) {
        queryClient.setQueryData(
          societyGuidesQueries.gettingStarted(variables.societyId).queryKey,
          data
        )
      }
      navigate(reverseUrl('management:getting-started', { id: society?._id }))
    },
  })

  if (!showGettingStarted || !society || !guide) {
    return null
  }

  const gettingStartedItem = guide?.steps?.find(
    (item) => item.id === gettingStartedId
  )
  const index =
    guide?.steps?.findIndex((item) => item.id === gettingStartedId) || 0
  const completed = !!gettingStartedItem?.completed
  return (
    <div
      className={`rounded-md bg-brandLightBrown border border-brandBrown p-5 w-fit ${className}`}
    >
      <div className="flex gap-2 mb-2">
        {completed ? (
          <Icon icon={IconChoices.CHECK_CIRCLE} highlighted />
        ) : (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: theme.colors.white,
              borderColor: theme.colors.almostBrandBrown,
              borderWidth: 1,
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <div style={theme.textVariants.base}>{index + 1}</div>
          </div>
        )}
        <p style={theme.textVariants.baseBold}>{title}</p>
      </div>
      <p style={{ ...theme.textVariants.base, marginBottom: theme.spacing.m }}>
        {description}
      </p>
      <div className="flex gap-4">
        <Button
          label={translate('common.actions.back')}
          onClick={() =>
            navigate(
              reverseUrl('management:getting-started', { id: society._id })
            )
          }
          icon={IconChoices.ARROW_LEFT}
        />

        {!hideCompleteButton && (
          <Button
            variant={ButtonVariant.PRIMARY}
            label={
              completed
                ? translate('gettingStartedScreen.screens.button.undo')
                : translate('gettingStartedScreen.screens.button.complete')
            }
            loading={isPending}
            onClick={() => {
              mutate({
                completed: !completed,
                id: gettingStartedId,
                societyId: society._id,
              })
            }}
          />
        )}
      </div>
    </div>
  )
}

export default GettingStarted
